import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, ButtonProps, Icon } from 'design-system-web';

import styles from './FeedbackButton.scss';

import { ServiceRequestStatusEnum } from '../types/serviceRequestStatus';
import { ServiceRequestFeedbackModal } from './ServiceRequestFeedbackModal';
import { WorkOrderModuleSettings } from 'lane-shared/domains/workOrder';
import { useSurveySubmissionEligibility } from './useSurveySubmissionEligibility';

type ServiceRequestFeedback = {
  rating: number;
  comment?: string | null;
  submittedAt: string;
  userId: string;
} | null;

export type ServiceRequestData = {
  serviceRequestId: string;
  userFriendlyID: string;
  status: string;
  category?: string;
  issue?: string;
};

export type FeedbackButtonProps = {
  serviceRequestStatus: ServiceRequestStatusEnum;
  serviceRequest: ServiceRequestData;
  feedback?: ServiceRequestFeedback;
  onFeedbackSubmit?: () => void;
  settings: WorkOrderModuleSettings;
};

export const FeedbackButton = ({
  serviceRequestStatus,
  serviceRequest,
  feedback,
  onFeedbackSubmit,
  settings,
}: FeedbackButtonProps) => {
  const { t } = useTranslation();

  const [serviceRequestModalOpen, setServiceRequestModalOpen] = useState(false);

  const { isSurveySubmissionEligible: canShowFeedback } = useSurveySubmissionEligibility(settings, serviceRequestStatus);
  const hasFeedback = !!feedback;

  // Don't render if feedback cannot be left and hasn't been left
  if (!canShowFeedback && !hasFeedback) {
    return null;
  }

  // Button configuration based on feedback state
  const buttonConfig: Pick<
    ButtonProps,
    'variant' | 'className' | 'startIcon' | 'disabled' | 'children'
  > = hasFeedback
    ? {
        variant: 'text',
        className: styles.leftFeedbackButton,
        startIcon: <Icon name="check" />,
        disabled: true,
        children: t('web.member.serviceRequest.feedback.left'),
      }
    : {
        variant: 'secondary',
        className: styles.leaveFeedbackButton,
        startIcon: <Icon name="star" />,
        disabled: false,
        children: t('web.member.serviceRequest.feedback.leave'),
      };

  return (
    <>
      <Button
        size="medium"
        onClick={() => setServiceRequestModalOpen(true)}
        {...buttonConfig}
        testId="feedback-button"
      />
      <ServiceRequestFeedbackModal
        isOpen={serviceRequestModalOpen}
        onClose={() => setServiceRequestModalOpen(false)}
        onFeedbackSubmit={onFeedbackSubmit}
        serviceRequest={serviceRequest}
        testId="service-request-feedback-modal"
      />
    </>
  );
};
